import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Send, Check, History, FileText, Lock, ClipboardList, AlertTriangle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fileURL = (u) => (!u ? null : u.startsWith('http') ? u : `${BACKEND_URL}${u}`);

// Sections the workbench reviser is allowed to touch (kept in sync with backend).
const REVISABLE_KEYS = ['FIRST FRAME', 'ENVIRONMENT', 'SUBJECT', 'CINEMATOGRAPHY'];

// Split a director prompt into [{header, body, revisable}] for the diff view.
function splitSections(prompt) {
  if (!prompt) return [];
  const re = /(?:^|\n)([A-Z][A-Z0-9 /&()\-]+):[ \t]*(?=\n)/g;
  const heads = [];
  let m;
  while ((m = re.exec(prompt)) !== null) {
    heads.push({ header: m[1].trim(), start: m.index + (m[0].startsWith('\n') ? 1 : 0), bodyStart: re.lastIndex });
  }
  if (!heads.length) return [{ header: null, body: prompt, revisable: false }];
  const out = [];
  const pre = prompt.slice(0, heads[0].start).trim();
  if (pre) out.push({ header: null, body: pre, revisable: false });
  heads.forEach((h, i) => {
    const bodyEnd = i + 1 < heads.length ? heads[i + 1].start : prompt.length;
    out.push({
      header: h.header,
      body: prompt.slice(h.bodyStart, bodyEnd).trim(),
      revisable: REVISABLE_KEYS.some((k) => h.header.includes(k)),
    });
  });
  return out;
}

// Author-model badge — Fable / Opus-revised / Opus-fallback at a glance.
export function AuthorModelBadge({ model, kind, className = '' }) {
  let label = 'Fable', cls = 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30';
  if (kind === 'opus-revised') { label = 'Opus-revised'; cls = 'bg-violet-500/15 text-violet-300 border-violet-500/30'; }
  else if (kind === 'opus-fallback' || (model && model.includes('opus') && kind !== 'opus-revised')) {
    label = model && model.includes('opus') && kind === 'base' ? 'Opus-fallback' : 'Opus'; cls = 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  } else if (model && model.includes('opus')) { label = 'Opus'; cls = 'bg-amber-500/15 text-amber-300 border-amber-500/30'; }
  if (!model) return null;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${cls} ${className}`} data-testid="author-model-badge">
      {label}
    </span>
  );
}

export function SourceBadge({ source, className = '' }) {
  if (!source) return null;
  const map = {
    manual_edit: ['Manual edit', 'bg-sky-500/15 text-sky-300 border-sky-500/30'],
    workbench_iterate: ['Workbench', 'bg-violet-500/15 text-violet-300 border-violet-500/30'],
    workbench_approve: ['Workbench', 'bg-violet-500/15 text-violet-300 border-violet-500/30'],
    base: ['Base', 'bg-white/10 text-white/60 border-white/20'],
    original: ['Original', 'bg-white/10 text-white/60 border-white/20'],
  };
  const [label, cls] = map[source] || [source, 'bg-white/10 text-white/60 border-white/20'];
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${cls} ${className}`} data-testid="round-source-badge">
      {label}
    </span>
  );
}

export default function NapkinWorkbench({ open, onClose, projectId, shotId, shotLabel, onApproved }) {
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [lastError, setLastError] = useState(null); // {message, feedback} — in-card named error + Retry
  const pollRef = useRef(null);

  // Stop polling when the modal closes / unmounts.
  useEffect(() => {
    if (!open && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [open]);

  const load = useCallback(async (isStart) => {
    try {
      const url = `${API}/projects/${projectId}/shots/${shotId}/napkin-workbench${isStart ? '/start' : ''}`;
      const res = isStart ? await axios.post(url, {}) : await axios.get(url);
      setState(res.data);
      const rounds = res.data.rounds || [];
      setSelected((prev) => prev || (rounds.length ? rounds[rounds.length - 1].round_id : null));
      return res.data;
    } catch (e) {
      toast.error(`Workbench: ${e.response?.data?.detail || e.message}`);
      return null;
    }
  }, [projectId, shotId]);

  const startPolling = useCallback((roundId) => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setBusy(true);
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > 8 * 60 * 1000) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
        setLastError({ message: 'Revise is taking unusually long — the render may still be finishing. Retry, or reopen shortly.', feedback: null });
        return;
      }
      const data = await load(false);
      const rnd = (data?.rounds || []).find((r) => r.round_id === roundId);
      if (!rnd || rnd.status === 'generating') return;
      clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      if (rnd.status === 'error') setLastError({ message: rnd.error || 'Generation error', feedback: rnd.feedback || null });
      else { setLastError(null); toast.success(`Round ${rnd.round_index} — napkin revised`); }
    }, 3000);
  }, [load]);

  // On open (and req 4: survive a page refresh) — load state and, if a round is
  // still generating, auto-resume the spinner + polling instead of a dead state.
  useEffect(() => {
    if (!open) return;
    // Clear the PREVIOUS shot's state so its workbench doesn't flash before the
    // current shot loads (UX fix 2026-07-15). Show a brief loading state instead.
    setState(null);
    setSelected(null);
    setFeedback('');
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setBusy(false);
    (async () => {
      const data = await load(true);
      const gen = (data?.rounds || []).find((r) => r.status === 'generating');
      if (gen) { setSelected(gen.round_id); startPolling(gen.round_id); }
    })();
  }, [open, shotId, load, startPolling]);

  const iterate = async (overrideFeedback, baseRoundIdOverride) => {
    const fb = (typeof overrideFeedback === 'string' ? overrideFeedback : feedback).trim();
    if (!fb) { toast.error('Enter composition feedback first'); return; }
    if (busy) return;  // req 2: guard against double-billing from rapid clicks
    setLastError(null);  // clear any prior in-card error on a fresh attempt
    // Iterate from the round the user is VIEWING (branch), not just the latest.
    const baseRoundId = baseRoundIdOverride || selected || undefined;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/shots/${shotId}/napkin-workbench/iterate`, { feedback: fb, base_round_id: baseRoundId });
      // ASYNC (CD-3): the round comes back 'generating'. Show it immediately,
      // then poll until it flips to ready/error — the request no longer blocks
      // on the ~60-120s generation, so the live-domain proxy timeout can't turn
      // Revise into a bare "Network Error".
      const newRoundId = res.data.round.round_id;
      setState(res.data.state);
      setSelected(newRoundId);
      setFeedback('');
      startPolling(newRoundId);
    } catch (e) {
      // req 3: a POST-level error may still have landed the round server-side.
      // Re-check before declaring failure, and adopt the round if it exists.
      const status = e.response?.status;
      if (status === 409) {
        // A revision is already generating — resume against it instead of erroring.
        const data = await load(false);
        const gen = (data?.rounds || []).find((r) => r.status === 'generating');
        if (gen) { setSelected(gen.round_id); startPolling(gen.round_id); return; }
      }
      if (!e.response) {
        const data = await load(false);
        const gen = (data?.rounds || []).find((r) => r.status === 'generating' || (r.feedback === fb && r.source === 'workbench_iterate'));
        if (gen) { setSelected(gen.round_id); if (gen.status === 'generating') startPolling(gen.round_id); else setBusy(false); return; }
      }
      setBusy(false);
      setLastError({ message: e.response?.data?.detail || e.message || 'Revise failed', feedback: fb });
    }
  };

  const approve = async () => {
    if (!selected) return;
    setApproving(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/shots/${shotId}/napkin-workbench/approve`, { round_id: selected });
      setState(res.data.state);
      toast.success('Approved — canonical prompt updated');
      if (onApproved) onApproved();
    } catch (e) {
      toast.error(`Approve failed: ${e.response?.data?.detail || e.message}`);
    } finally { setApproving(false); }
  };

  if (!open) return null;
  const rounds = state?.rounds || [];
  const sel = rounds.find((r) => r.round_id === selected) || rounds[rounds.length - 1];
  const isApproved = state?.approved_round_id && sel && state.approved_round_id === sel.round_id;
  const canonicalPrompt = state?.canonical_keyframe_prompt || '';
  const sections = splitSections(sel?.prompt || '');
  const appliedHeaders = new Set(sel?.applied_sections || []);
  const isDraft = sel && sel.prompt !== canonicalPrompt;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="napkin-workbench-backdrop">
      <div className="w-full max-w-5xl h-[90vh] flex flex-col rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(13,18,32,0.99)' }} onClick={(e) => e.stopPropagation()} data-testid="napkin-workbench">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-semibold text-white">Napkin Workbench</span>
            <span className="text-[11px] text-white/50 font-mono">{shotLabel || shotId}</span>
            {state?.approval && <span className="text-[10px] text-emerald-300">· approved R{state.approval.round_index} by {state.approval.user}</span>}
          </div>
          <div className="flex items-center gap-2">
            {state?.approval_log?.length ? (
              <button onClick={() => setShowAudit((v) => !v)} className="flex items-center gap-1 px-2 py-1 rounded border border-white/15 text-white/60 hover:text-white/90 hover:border-white/30 text-[10px]" data-testid="napkin-audit-toggle">
                <ClipboardList className="w-3 h-3" /> Audit ({state.approval_log.length})
              </button>
            ) : null}
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="napkin-workbench-close"><X className="w-4 h-4 text-white/60" /></button>
          </div>
        </div>

        {showAudit && state?.approval_log?.length ? (
          <div className="px-5 py-2 border-b border-white/10 bg-black/30 max-h-32 overflow-y-auto" data-testid="napkin-audit-log">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Approval history</div>
            {[...state.approval_log].reverse().map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] text-white/70 py-0.5" data-testid={`napkin-audit-entry-${i}`}>
                <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                <span className="font-mono">R{a.round_index}</span>
                <span className="text-white/50">·</span>
                <span>{a.user}</span>
                <span className="text-white/40 ml-auto">{a.approved_at ? new Date(a.approved_at).toLocaleString() : ''}</span>
                <AuthorModelBadge model={a.author_model} kind={(a.author_model || '').includes('opus') ? 'opus-revised' : 'base'} />
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex-1 min-h-0 flex">
          {/* Main image + feedback */}
          <div className="flex-1 min-w-0 flex flex-col p-4 gap-3">
            <div className="flex-1 min-h-0 flex items-center justify-center bg-black/40 rounded-xl border border-white/5 overflow-hidden">
              {sel?.status === 'generating' ? (
                <div className="flex flex-col items-center gap-3 text-white/60" data-testid="napkin-round-generating">
                  <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
                  <div className="text-sm">Revising napkin…</div>
                  <div className="text-[11px] text-white/40">Opus rewrite + regeneration · ~1–2 min · safe to wait</div>
                </div>
              ) : sel?.status === 'error' ? (
                <div className="flex flex-col items-center gap-2 text-rose-300 px-6 text-center" data-testid="napkin-round-error">
                  <X className="w-7 h-7" />
                  <div className="text-sm font-semibold">Revise failed</div>
                  <div className="text-[11px] text-rose-200/70">{sel.error || 'generation error'}</div>
                  <button
                    onClick={() => iterate(sel.feedback || '', sel.base_round_id)}
                    disabled={busy || !sel.feedback}
                    className="mt-2 px-3 py-1.5 rounded-lg bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 border border-violet-500/40 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="napkin-round-retry-btn"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Retry this revision
                  </button>
                </div>
              ) : sel?.napkin_url ? (
                <img src={fileURL(sel.napkin_url)} alt="napkin round" className="max-h-full max-w-full object-contain" data-testid="napkin-workbench-image" />
              ) : !state ? (
                <div className="flex flex-col items-center gap-2 text-white/40" data-testid="napkin-workbench-loading">
                  <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
                  <span className="text-sm">Loading workbench…</span>
                </div>
              ) : (
                <div className="text-white/40 text-sm">No napkin for this round</div>
              )}
            </div>
            <div className="flex items-center gap-2 text-[11px] text-white/60">
              <span>Round {sel?.round_index ?? 0}</span>
              <AuthorModelBadge model={sel?.author_model} kind={sel?.author_kind} />
              <SourceBadge source={sel?.source} />
              {sel?.user ? <span className="text-white/40" data-testid="napkin-round-user">{sel.user}{sel?.created_at ? ` · ${new Date(sel.created_at).toLocaleString()}` : ''}</span> : null}
              {sel?.applied_sections?.length ? <span className="text-white/40">revised: {sel.applied_sections.join(', ')}</span> : null}
              {typeof sel?.base_round_index === 'number' && sel.base_round_index !== (sel.round_index - 1) ? (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold border bg-sky-500/15 text-sky-300 border-sky-500/30" data-testid="napkin-branch-badge">branched from R{sel.base_round_index}</span>
              ) : null}
              <span className="ml-auto" />
              {isDraft ? (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold border bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid="napkin-draft-badge">DRAFT · canonical unchanged</span>
              ) : (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold border bg-emerald-500/15 text-emerald-300 border-emerald-500/30" data-testid="napkin-canonical-badge">CANONICAL</span>
              )}
              <button onClick={() => setShowPrompt((v) => !v)} className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/15 text-white/60 hover:text-white/90 hover:border-white/30 text-[10px]" data-testid="napkin-toggle-prompt">
                <FileText className="w-3 h-3" /> {showPrompt ? 'Hide' : 'View'} prompt & diff
              </button>
            </div>

            {showPrompt && (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10 bg-black/40 p-3 space-y-2 text-[11px] leading-relaxed" data-testid="napkin-prompt-diff">
                {sel?.applied_sections?.length ? (
                  <div className="text-[10px] text-violet-300/80">Highlighted sections were revised this round; locked sections are byte-identical to R0.</div>
                ) : (
                  <div className="text-[10px] text-white/40">Base round — no revisions applied.</div>
                )}
                {sections.map((s, i) => {
                  const changed = s.header && appliedHeaders.has(s.header);
                  return (
                    <div key={i} className={`rounded p-1.5 ${changed ? 'bg-violet-500/10 border border-violet-500/30' : ''}`}>
                      {s.header && (
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`font-mono font-semibold ${changed ? 'text-violet-300' : s.revisable ? 'text-white/70' : 'text-white/50'}`}>{s.header}</span>
                          {!s.revisable && <Lock className="w-2.5 h-2.5 text-white/30" title="locked" />}
                          {changed && <span className="text-[9px] px-1 rounded bg-violet-500/20 text-violet-200">revised</span>}
                        </div>
                      )}
                      <div className={`whitespace-pre-wrap ${changed ? 'text-white/90' : 'text-white/55'}`}>{s.body}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {sel && sel.status !== 'generating' && sel.status !== 'error' && (
              <div className="text-[10px] text-white/40" data-testid="napkin-revise-base-hint">
                Revise will branch a new round from <span className="text-violet-300 font-semibold">R{sel.round_index}</span> (the round shown above).
              </div>
            )}
            {lastError && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="napkin-revise-error">
                <AlertTriangle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-red-200 font-semibold">Revise failed</div>
                  <div className="text-[10px] text-red-200/70 break-words" data-testid="napkin-revise-error-msg">{lastError.message}</div>
                </div>
                <button
                  onClick={() => iterate(lastError.feedback || sel?.feedback || feedback)}
                  disabled={busy}
                  className="px-2.5 py-1 rounded-md bg-red-500/20 text-red-100 hover:bg-red-500/30 border border-red-500/40 text-[11px] font-semibold flex items-center gap-1 disabled:opacity-50 shrink-0"
                  data-testid="napkin-revise-retry">
                  <RefreshCw className="w-3 h-3" /> Retry
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                value={feedback} onChange={(e) => setFeedback(e.target.value)}
                placeholder="Composition feedback — framing, camera angle, subject placement, in/out of frame (napkins are grayscale; no colour/lighting notes here)…"
                className="flex-1 h-16 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white/90 text-[12px] resize-none focus:outline-none focus:border-violet-500/50"
                data-testid="napkin-feedback-input"
              />
              <button onClick={iterate} disabled={busy} className="px-3 rounded-lg bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 border border-violet-500/40 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid="napkin-iterate-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Revise
              </button>
            </div>
            <button onClick={approve} disabled={approving || !sel || sel?.status === 'generating' || sel?.status === 'error'} className={`w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border transition-all disabled:opacity-50 ${isApproved ? 'bg-emerald-500/25 text-emerald-200 border-emerald-500/50' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'}`} data-testid="napkin-approve-btn">
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isApproved ? `Approved (R${sel?.round_index}) — canonical` : selected === rounds[rounds.length - 1]?.round_id ? `Approve R${sel?.round_index}` : `Restore & Approve R${sel?.round_index}`}
            </button>
          </div>

          {/* Rounds strip */}
          <div className="w-40 flex-shrink-0 border-l border-white/10 overflow-y-auto p-2 space-y-2" data-testid="napkin-rounds-strip">
            {rounds.map((r) => {
              const isSel = r.round_id === selected;
              const isAppr = state?.approved_round_id === r.round_id;
              return (
                <button key={r.round_id} onClick={() => setSelected(r.round_id)}
                  className={`w-full rounded-lg overflow-hidden border-2 transition-all ${isSel ? 'border-violet-400' : 'border-white/10 hover:border-white/30'}`}
                  data-testid={`napkin-round-${r.round_index}`}>
                  <div className="aspect-video bg-black/40 flex items-center justify-center">
                    {r.status === 'generating'
                      ? <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                      : r.napkin_url
                        ? <img src={fileURL(r.napkin_url)} alt={`R${r.round_index}`} className="w-full h-full object-cover" />
                        : <span className="text-[10px] text-white/30">R{r.round_index}</span>}
                  </div>
                  <div className="flex items-center justify-between px-1.5 py-1 bg-black/50">
                    <span className="text-[10px] text-white/70 font-mono">R{r.round_index}{isAppr ? ' ✓' : ''}</span>
                    {r.author_model ? <AuthorModelBadge model={r.author_model} kind={r.author_kind} /> : <SourceBadge source={r.source} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
